import {
  computed,
  toValue,
  ref,
  watch,
  type Ref,
  type ComputedRef,
  type MaybeRefOrGetter
} from 'vue'
import { storeToRefs } from 'pinia'
import { useDataSettingStore } from '@/stores/dataSetting.stores'
import { useDataSettingStore as useDashboardAccountInquirySettingStore } from '../../stores/dataSetting.stores'
import { useSystemVersionStore } from '@/stores/systemVersion.stores'
import { useRoute } from 'vue-router'
import { getClawOperationsDetailQueryOptions } from '@/queries/dashboard-account-inquiry/queries'
import { useAppQuery } from '@/composables/useAppQuery'
import { useDate } from '@/composables/useDate'
import { useStoresSettlementTimezone } from '@/stores/storesSettlementTimezone.stores'
import { useLedgerExclusion } from '@/composables/useLedgerExclusion'
import type {
  LedgerAbnormalResType,
  LedgerCorrectionResType
} from '@/models/types/ledgerAnomaly.types'
import dayjs from 'dayjs'

// Persists the last valid API response across component remounts (keyed by pcbId)
const validDataCache = new Map<string, Ref<any>>()

// 這個 Map 活在 TanStack Query 的 queryClient 之外，帳號切換（ContextSwitch/返回主帳號）
// 時 queryClient.clear() 清不到它，需另外呼叫這支清掉，避免看到上一個帳號殘留的數字
export const clearClawValidDataCache = () => {
  validDataCache.clear()
}

type ClawDetailData = {
  pcbName: string
  cashboxAmount?: number
  totalRevenue: number
  prizeWinCount: number
  averagePrizeWinCount: number
  profit: number
  viewProfitAllowed: boolean
}

export const useClaw = (): {
  isLoading: ComputedRef<boolean>
  detail: ComputedRef<ClawDetailData>
  list: MaybeRefOrGetter<any>
  // OP#1855：日列標示要用到，由這裡一併提供，避免呼叫端重複組裝查詢參數
  abnormals: ComputedRef<LedgerAbnormalResType[]>
  corrections: ComputedRef<LedgerCorrectionResType[]>
} => {
  const dataSetting = useDataSettingStore()
  const { startDate, endDate } = storeToRefs(dataSetting)

  const dashboardSetting = useDashboardAccountInquirySettingStore()
  const {
    startDate: inquiryStartDate,
    endDate: inquiryEndDate,
    hasAppliedDateFilter,
    isTimeSettingsReady
  } = storeToRefs(dashboardSetting)

  const route = useRoute()
  const { isNewSystem } = storeToRefs(useSystemVersionStore())
  const { getEndDayOfThisWeek, getAccountingPeriod } = useDate()
  const { getSettlement, getSettlementTimeZone } = useStoresSettlementTimezone()

  const pcbId = route.params.pcbId as string

  // 尚未套用篩選（本週預設）時，endDate 是單純的「今天」，併入 endTime 後若原封不動送出會
  // 變成「今天 結帳時間」，把目前這個帳務日排除在外。改送目前帳務日的上限當終點。
  //
  // OP#2730：原本固定送「今天 + 1 天」，在還沒到今天的結帳點時已超出後端上限（上一個結帳點
  // + 24 小時），進帳務查詢頁的預設查詢直接回 400。套用篩選後 innerEndDate 已由 Modal 自己
  // 算好（同一套邏輯），直接送出即可。舊系統（isNewSystem=false）不會併入小時，維持原本的
  // 「今天」純日期送出
  const queryEndDate = computed(() =>
    isNewSystem.value && !hasAppliedDateFilter.value
      ? getAccountingPeriod(getSettlement(), getSettlementTimeZone()).upperBound.slice(0, 10)
      : inquiryEndDate.value
  )

  // fillMissingDates 補的是日曆天，只服務「本週預設」這一種情境：規格要求顯示完整本週日～
  // 本週六，但未來日期不會實際查詢（避免後端拒絕），所以用本週六（而非 inquiryEndDate 被封頂
  // 過的今天）當補齊終點，讓今天之後的日子補上空值。套用篩選後是使用者自己選的區間，要如實
  // 呈現後端回的資料，傳 null 代表不補值
  const fillEndDate = computed(() => (hasAppliedDateFilter.value ? null : getEndDayOfThisWeek()))

  // 跟機台列表 Modal 同一套 combineDateTime：isNewSystem 時一律併入小時送出，
  // 舊系統 combineDateTime 內部會自動退回純日期
  // enabled：等該店結算時間設定（GetStoreTimeZone）取得後才真的查詢，避免拿寫死的 00:00
  // 當預設時間送出真實查詢（帳務查詢頁沒有 Dashboard 那種 noDate 防呆機制）
  const clawDetailData: MaybeRefOrGetter<any> = useAppQuery(() => ({
    ...getClawOperationsDetailQueryOptions(
      {
        pcbId,
        startDate: dataSetting.combineDateTime(inquiryStartDate.value, dataSetting.startTime),
        endDate: dataSetting.combineDateTime(queryEndDate.value, dataSetting.endTime)
      },
      fillEndDate.value
    ),
    enabled: isTimeSettingsReady.value
  }))

  // OP#1855：系統偵測的異常帳目。被判定為拒絕的事件要從帳務數字中扣除
  const { abnormals, corrections } = useLedgerExclusion({
    pcbId,
    startAt: () => dataSetting.combineDateTime(inquiryStartDate.value, dataSetting.startTime),
    endAt: () => dataSetting.combineDateTime(queryEndDate.value, dataSetting.endTime),
    enabled: () => isTimeSettingsReady.value
  })

  if (!validDataCache.has(pcbId)) {
    validDataCache.set(pcbId, ref(null))
  }
  const lastValidData = validDataCache.get(pcbId)!

  watch(
    () => toValue(clawDetailData.data),
    (newData) => {
      if (newData != null) lastValidData.value = newData
    }
  )

  const detail = computed(() => {
    const detail = toValue(clawDetailData.data) ?? lastValidData.value

    if (!detail) {
      return {
        pcbName: '',
        cashboxAmount: 0,
        totalRevenue: 0,
        prizeWinCount: 0,
        averagePrizeWinCount: 0,
        profit: 0,
        viewProfitAllowed: true
      }
    }

    const list = detail.records.filter((record: any) => {
      // OP#2373：真實結算區間以 startTime 為準；fillMissingDates 補齊的預設值列沒有
      // startTime/endTime，退回用 date 判斷
      const recordDate = dayjs(record.startTime ?? record.date)

      return (
        dayjs(recordDate).isSame(dayjs(startDate.value), 'day') ||
        dayjs(recordDate).isSame(dayjs(endDate.value), 'day') ||
        (dayjs(recordDate).isAfter(dayjs(startDate.value)) &&
          dayjs(recordDate).isBefore(dayjs(endDate.value)))
      )
    })

    // OP#1855：被判定排除的帳目由後端在數字上處理掉，前端不自行扣除，
    // 兩邊都扣會變成雙重扣除。這裡維持單純加總後端給的值
    const totalRevenue = list.reduce((acc: any, curr: any) => acc + curr.revenue, 0)
    const prizeWinCount = list.reduce((acc: any, curr: any) => acc + curr.prizeWinCount, 0)
    const averagePrizeWinCount =
      prizeWinCount > 0 ? Number((totalRevenue / prizeWinCount).toFixed(2)) : 0
    const profit = list.reduce(
      (acc: any, curr: any) => acc + (curr.profit !== null ? curr.profit : 0),
      0
    )

    return {
      pcbName: detail.pcbName,
      cashboxAmount: detail.cashboxAmount,
      totalRevenue,
      prizeWinCount,
      averagePrizeWinCount,
      profit,
      // profit 可能被後端隱藏，卡片需跟著帶出顯示權限，不能只靠前端自行推導
      viewProfitAllowed: list.length > 0 ? list[0].profit !== null : true
    }
  })

  const list = computed(() => toValue(clawDetailData.data) ?? lastValidData.value)

  return {
    isLoading: computed(() => !isTimeSettingsReady.value || !!toValue(clawDetailData.isFetching)),
    detail,
    list,
    abnormals,
    corrections
  }
}
